import React, { useEffect, useState } from 'react';
import { Customer } from '../types';
import { Button, Field, Input, Modal, Select } from './ui';

interface AddCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (customer: Customer) => void;
  initialData?: Customer | null;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  loyaltyStatus: Customer['loyaltyStatus'];
}

const blank: FormState = {
  name: '',
  email: '',
  phone: '',
  loyaltyStatus: 'New',
};

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
}) => {
  const [formData, setFormData] = useState<FormState>(blank);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>(
    {}
  );

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        email: initialData.email,
        phone: initialData.phone || '',
        loyaltyStatus: initialData.loyaltyStatus,
      });
    } else {
      setFormData(blank);
    }
    setErrors({});
  }, [initialData, open]);

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!formData.name.trim()) next.name = 'Name is required';
    if (!formData.email.trim()) next.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      next.email = 'Enter a valid email address';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const customer: Customer = {
      id:
        initialData?.id ||
        `c${Math.random().toString(36).substring(2, 11)}`,
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      loyaltyStatus: formData.loyaltyStatus,
      totalSpent: initialData?.totalSpent || 0,
      bookings: initialData?.bookings || [],
      contacts: initialData?.contacts || [],
    };
    onSubmit(customer);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={initialData ? 'Edit customer' : 'New customer'}
      description={
        initialData
          ? 'Update this guest’s details.'
          : 'Add a guest to your CRM.'
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="customer-form">
            {initialData ? 'Save changes' : 'Create customer'}
          </Button>
        </div>
      }
    >
      <form id="customer-form" onSubmit={handleSubmit} className="space-y-4">
        <Field
          label="Full name"
          htmlFor="cust-name"
          required
          error={errors.name}
        >
          <Input
            id="cust-name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g. Emma Harrison"
            autoFocus
          />
        </Field>

        <Field
          label="Email"
          htmlFor="cust-email"
          required
          error={errors.email}
        >
          <Input
            id="cust-email"
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            placeholder="emma@example.com"
          />
        </Field>

        <Field label="Phone" htmlFor="cust-phone">
          <Input
            id="cust-phone"
            type="tel"
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            placeholder="+44 7700 000000"
          />
        </Field>

        <Field label="Loyalty status" htmlFor="cust-loyalty">
          <Select
            id="cust-loyalty"
            value={formData.loyaltyStatus}
            onChange={(e) =>
              setFormData({
                ...formData,
                loyaltyStatus: e.target.value as Customer['loyaltyStatus'],
              })
            }
          >
            <option value="New">New guest</option>
            <option value="Bronze">Bronze</option>
            <option value="Silver">Silver</option>
            <option value="Gold">Gold</option>
          </Select>
        </Field>
      </form>
    </Modal>
  );
};

export default AddCustomerModal;
